/* PackFlow Cart Rules - normalizes cart on every mutation
 * Rules (scoped per `_session_id` property = per design):
 *   1. Free-with-print: if a print SKU is in cart for a session, any downloads
 *      it covers (in the same session) are redundant and removed.
 *      - print_*f (front-only)  covers: download_logo, download_front, download_logo_front
 *      - print_*fb (front+back) covers: ALL downloads
 *   2. Bundle supersedes individuals (same session):
 *      - download_bundle covers: logo, front, back, logo_front (and any front_back)
 *      - download_logo_front covers: logo, front
 *   3. Higher-tier print supersedes lower-tier same quantity (same session):
 *      - print_*fb supersedes print_*f at the same quantity
 *   4. Download lines: quantity capped at 1 (you can't buy the same file twice).
 *   5. Banner: non-blocking explanation of any auto-removals.
 *
 * Cross-session items (different designs) NEVER trigger removals against each other.
 */
(function () {
  'use strict';
  if (window._pfCartRulesLoaded) return;
  window._pfCartRulesLoaded = true;

  // -------------------------------------------------------------------------
  // SKU catalog (kept in sync with pod/config.json -> shopify_packflow.alacarte_products)
  // We match by _item_id property (set by the app at add-to-cart time) AND fall
  // back to product_title pattern matching as a safety net for legacy items.
  // -------------------------------------------------------------------------
  var DOWNLOADS = {
    download_logo:        { covers: ['download_logo'] },
    download_front:       { covers: ['download_front'] },
    download_back:        { covers: ['download_back'] },
    download_logo_front:  { covers: ['download_logo', 'download_front', 'download_logo_front'] },
    download_front_back:  { covers: ['download_front', 'download_back', 'download_front_back'] },
    download_bundle:      { covers: ['download_logo', 'download_front', 'download_back',
                                     'download_logo_front', 'download_front_back', 'download_bundle'] }
  };

  // Variant IDs for the standalone download SKUs we may auto-add when a bundle
  // gets removed because a print partially covers it. Source of truth is
  // packflow/server/routes/checkout.js. Update both files together.
  var VARIANTS = {
    download_logo:  42851896000610,
    download_front: 42851896033378,
    download_back:  42851896098914
  };

  // Rush production variants. The cart-wide rush toggle (on the /cart page)
  // adds two lines when active:
  //   - rush_base_fee:    qty 1, $50    (one per cart)
  //   - rush_per_sticker: qty = max(0, total_stickers - 32) at $0.25 each
  // Total rush charge: $50 + $0.25 × max(0, stickers − 32). Auto-synced by
  // planRushSync() below — toggle stores state in cart.attributes.rush_production.
  // Source of truth: pod/config.json -> shopify_packflow.rush_production.
  var RUSH = {
    base:        { variantId: 42881827274850, itemId: 'rush_base_fee',     title: 'Rush Production — base fee' },
    perSticker:  { variantId: 42881827307618, itemId: 'rush_per_sticker',  title: 'Rush Production — per sticker' }
  };
  var RUSH_VARIANT_IDS = { 42881827274850: 'base', 42881827307618: 'perSticker' };
  // Legacy rush variants from R13/R20 — if any of these still appear in a
  // customer's cart we strip them (they're no longer valid SKUs).
  var LEGACY_RUSH_VARIANTS = {
    42852623843426: true, 42852623876194: true, 42852623745122: true,
    42852623777890: true, 42852623810658: true, 42852623909986: true,
    42852623942754: true
  };

  // Digital product handles — used by the split-delivery banner to classify
  // cart contents as digital vs physical. Source of truth:
  //   - pod/config.json -> shopify_packflow.alacarte_products (download_*)
  //   - server/lib/promoConfig.js (promo_* / *-promo-package)
  // Keep this list in sync when new digital SKUs are added.
  var DIGITAL_HANDLES = {
    'logo-download': true,
    'front-design-download': true,
    'back-design-download': true,
    'logo-front-bundle': true,
    'full-bundle-logo-front-back': true,
    'starter-promo-package': true,
    'standard-promo-package': true,
    'pro-promo-package': true
  };
  // Substring patterns for forward-compat — any handle containing one of
  // these counts as digital even if the exact handle changes.
  var DIGITAL_HANDLE_PATTERNS = [/^download(-|$)/i, /-download($|-)/i, /^promo-/i, /-promo-package$/i];

  var PRINTS_FRONT_ONLY = ['print_32f', 'print_64f', 'print_128f'];
  var PRINTS_BOTH_SIDES = ['print_32fb', 'print_64fb', 'print_128fb'];
  // Sticker count per print SKU — used to compute rush per-sticker quantity
  // and to decide whether the rush toggle should appear on the cart page.
  var PRINT_QTY = { print_32f: 32, print_32fb: 32, print_64f: 64, print_64fb: 64, print_128f: 128, print_128fb: 128 };
  var STICKERS_PER_SKU = { print_32f: 32, print_32fb: 64, print_64f: 64, print_64fb: 128, print_128f: 128, print_128fb: 256 };
  var PRINT_SIDES = {
    print_32f: 'f', print_64f: 'f', print_128f: 'f',
    print_32fb: 'fb', print_64fb: 'fb', print_128fb: 'fb'
  };

  // What ATOMIC downloads (logo / front / back) does a given print sku
  // make redundant? Returns the list of atomic file IDs the print includes
  // free with the physical order. Bundles are handled by overlap below.
  function printCoversAtoms(itemId) {
    if (PRINTS_BOTH_SIDES.indexOf(itemId) > -1) {
      return ['download_logo', 'download_front', 'download_back'];
    }
    if (PRINTS_FRONT_ONLY.indexOf(itemId) > -1) {
      return ['download_logo', 'download_front'];
    }
    return [];
  }

  // Backwards-compat alias — returns the FULL list of download SKUs (atomic +
  // bundle SKUs) that are FULLY covered by this print. A bundle is fully
  // covered only if the print covers EVERY atom in the bundle. Front-only
  // prints do NOT fully cover front_back or bundle (they miss the back).
  function printCovers(itemId) {
    var atoms = printCoversAtoms(itemId);
    var atomSet = {};
    atoms.forEach(function (a) { atomSet[a] = true; });
    var fully = atoms.slice();
    Object.keys(DOWNLOADS).forEach(function (dlId) {
      // The bundle's atoms = its covers list minus its own self-id.
      var bundleAtoms = (DOWNLOADS[dlId].covers || []).filter(function (c) {
        return c !== dlId && c.indexOf('_logo') > -1 || c === 'download_logo' || c === 'download_front' || c === 'download_back';
      });
      // Above filter is messy — use a simpler check: bundle atoms are the
      // covers that are themselves atomic SKUs.
      var atomicCovers = (DOWNLOADS[dlId].covers || []).filter(function (c) {
        return ['download_logo','download_front','download_back'].indexOf(c) > -1;
      });
      // Skip atomic SKUs themselves — already in atoms.
      if (atomicCovers.length === 0) return;
      if (['download_logo','download_front','download_back'].indexOf(dlId) > -1) return;
      var allCovered = atomicCovers.every(function (a) { return atomSet[a]; });
      if (allCovered) fully.push(dlId);
    });
    return fully;
  }

  // Does this print PARTIALLY cover this download bundle?
  // Returns the array of atoms the bundle still needs (the "remainder"),
  // or null if there's no overlap, or [] if fully covered.
  function printPartiallyCovers(printId, downloadId) {
    var atoms = printCoversAtoms(printId);
    if (!atoms.length) return null;
    var atomSet = {};
    atoms.forEach(function (a) { atomSet[a] = true; });
    // What atomic SKUs does this download bundle contain?
    var dlAtoms = (DOWNLOADS[downloadId] || { covers: [] }).covers.filter(function (c) {
      return ['download_logo','download_front','download_back'].indexOf(c) > -1;
    });
    if (dlAtoms.length === 0) return null;
    var overlap = dlAtoms.filter(function (a) { return atomSet[a]; });
    if (overlap.length === 0) return null;
    var remainder = dlAtoms.filter(function (a) { return !atomSet[a]; });
    return remainder; // [] = fully covered, [...] = partially covered, null = no overlap
  }

  function isDownload(itemId) { return DOWNLOADS.hasOwnProperty(itemId); }
  function isPrint(itemId)    { return PRINT_QTY.hasOwnProperty(itemId); }
  // 2026-05-20 Round 2: promo packages include all download files. Detect by
  // _item_id prefix 'promo_' (server-canonical: promo_starter|standard|pro).
  function isPromo(itemId)    { return typeof itemId === 'string' && itemId.indexOf('promo_') === 0; }

  // Read item id from cart line. Prefer property, fall back to title heuristics.
  function itemIdOf(line) {
    if (!line) return null;
    var p = line.properties || {};
    if (p._item_id) return p._item_id;
    if (p.item_id) return p.item_id;
    var title = (line.product_title || line.title || '').toLowerCase();
    // Heuristics for legacy lines without _item_id
    if (/full bundle/.test(title)) return 'download_bundle';
    if (/logo \+ front/.test(title)) return 'download_logo_front';
    if (/front \+ back/.test(title) && /download/.test(title)) return 'download_front_back';
    if (/logo download/.test(title)) return 'download_logo';
    if (/front design download/.test(title)) return 'download_front';
    if (/back design download/.test(title)) return 'download_back';
    var m = title.match(/(\d+)\s*bags?\s*(front\s*\+\s*back|front\s*only|front)/);
    if (m) {
      var qty = m[1], sides = /back/.test(m[2]) ? 'fb' : 'f';
      var id = 'print_' + qty + sides;
      if (PRINT_QTY[id]) return id;
    }
    return null;
  }

  // Group lines by design.
  //   - Preferred: matching _design_id (set by the app from selectedDesignId)
  //   - Fallback : if a line has NO _design_id but has _session_id, it groups
  //                with other no-design-id lines that share that _session_id.
  //                (Legacy items added before _design_id was wired.)
  //   - Anything else gets its own singleton bucket so it never triggers
  //     auto-removals against other lines.
  // Cross-bucket lines NEVER trigger removals (different designs = independent).
  function bucketKey(line) {
    var p = (line && line.properties) || {};
    if (p._design_id || p.design_id) return 'D:' + (p._design_id || p.design_id);
    if (p._session_id || p.session_id) return 'S:' + (p._session_id || p.session_id);
    // Singleton bucket per cart line key so it can’t falsely group with anything else
    return 'X:' + (line.key || (line.id + '_' + Math.random()));
  }
  function groupByDesign(items) {
    var groups = {};
    items.forEach(function (line, idx) {
      var k = bucketKey(line);
      if (!groups[k]) groups[k] = [];
      groups[k].push({ line: line, idx: idx, itemId: itemIdOf(line) });
    });
    return groups;
  }

  // Diagnostic logger — always logs to console when running so we can see
  // exactly what cart-rules touched. Look for [pf-cart-rules] in devtools
  // when investigating a missing-line bug (e.g. rush production dropped).
  function pfLog() {
    try {
      var args = Array.prototype.slice.call(arguments);
      args.unshift('[pf-cart-rules]');
      console.log.apply(console, args);
    } catch (_) {}
  }

  // Decide which lines to remove. Returns array of { key, reason }.
  // Also returns `additions` — standalone downloads to AUTO-ADD when a
  // bundle is removed but a remainder atom is still wanted by the customer.
  function planRemovals(cart) {
    var removals = [];
    var additions = []; // { variantId, itemId, properties, because }
    var seenKeys = {};
    var groups = groupByDesign(cart.items || []);

    Object.keys(groups).forEach(function (sid) {
      var group = groups[sid];
      pfLog('group', sid, 'items:', group.map(function(g){ return { itemId: g.itemId, key: g.line.key, title: g.line.product_title }; }));
      // Singleton buckets (single line, no shared design) can’t cause cross-line conflicts
      if (group.length < 2) return;

      // Find prints and downloads in this session
      var prints = group.filter(function (g) { return isPrint(g.itemId); });
      var downloads = group.filter(function (g) { return isDownload(g.itemId); });
      pfLog('  prints:', prints.map(function(g){return g.itemId;}), 'downloads:', downloads.map(function(g){return g.itemId;}));

      // Rule 1 (STRICT, per Jacques 2026-05-08): for any given design, the
      // cart must contain EITHER a print OR downloads, NEVER both.
      // Rationale: a print order "includes the design files free" — buying
      // download SKUs alongside is always redundant from the customer's
      // POV, even if specific atoms (e.g. back) aren't physically printed
      // on a front-only run. If the customer wants the back design as a
      // file, the upsell is to upgrade to a front+back print, not to keep
      // a download alongside.
      //
      // We don't auto-swap to standalone atoms (Round 10 logic dropped):
      //   1. It silently changed the customer's selection in confusing ways.
      //   2. It risked feedback loops where the auto-add itself triggered
      //      another normalize pass, sometimes duplicating lines.
      //
      // Cross-design lines are unaffected (groups are keyed by _design_id).
      if (prints.length > 0 && downloads.length > 0) {
        downloads.forEach(function (d) {
          if (seenKeys[d.line.key]) return;
          seenKeys[d.line.key] = true;
          removals.push({
            key: d.line.key,
            reason: 'included-with-print',
            itemId: d.itemId,
            because: prints[0].itemId,
            sessionId: sid
          });
        });
      }

      // Rule 1.5 (Round 2 of 3, 2026-05-20): a promo package in the same
      // cart as a download for the same design (or session) bundles the
      // download for free — every tier includes all design files. Mirror
      // the free-with-print pattern: remove the download line. The customer
      // still gets the download via the promo.
      var promos = group.filter(function (g) { return isPromo(g.itemId); });
      if (promos.length > 0 && downloads.length > 0) {
        downloads.forEach(function (d) {
          if (seenKeys[d.line.key]) return;
          seenKeys[d.line.key] = true;
          console.log('[pf-cart-rules] removed download ' + d.itemId + ' because promo ' + promos[0].itemId + ' is in cart (bundled)');
          removals.push({
            key: d.line.key,
            reason: 'included-with-promo',
            itemId: d.itemId,
            because: promos[0].itemId,
            sessionId: sid
          });
        });
      }

      // Rule 2: Bundle supersedes individuals (only among NOT-already-removed downloads)
      var remainingDownloads = downloads.filter(function (d) { return !seenKeys[d.line.key]; });
      // For each download line, check if any OTHER download line covers it
      remainingDownloads.forEach(function (d) {
        if (seenKeys[d.line.key]) return;
        for (var i = 0; i < remainingDownloads.length; i++) {
          var other = remainingDownloads[i];
          if (other.line.key === d.line.key) continue;
          if (seenKeys[other.line.key]) continue;
          // does `other` cover `d`?
          var otherCovers = (DOWNLOADS[other.itemId] || {}).covers || [];
          if (otherCovers.indexOf(d.itemId) > -1 && other.itemId !== d.itemId) {
            seenKeys[d.line.key] = true;
            removals.push({
              key: d.line.key,
              reason: 'covered-by-bundle',
              itemId: d.itemId,
              because: other.itemId,
              sessionId: sid
            });
            break;
          }
        }
      });

      // Rule 2b: Partial-overlap dedupe
      // If two distinct download bundles overlap (e.g. download_logo_front +
      // download_front_back both contain download_front), the customer is
      // paying for the same file twice and neither bundle fully covers the
      // other. Detect this and consolidate to download_bundle (which covers
      // both) — handled by removing both and letting the UI suggest the
      // bundle, OR if download_bundle is also present it covers both anyway
      // via Rule 2. Here we just flag the overlap and remove the LATER-added
      // (higher index) bundle so the user keeps the one they added first.
      var stillRemaining = downloads.filter(function (d) { return !seenKeys[d.line.key]; });
      for (var ii = 0; ii < stillRemaining.length; ii++) {
        var a = stillRemaining[ii];
        if (seenKeys[a.line.key]) continue;
        var aCovers = (DOWNLOADS[a.itemId] || {}).covers || [];
        for (var jj = ii + 1; jj < stillRemaining.length; jj++) {
          var b = stillRemaining[jj];
          if (seenKeys[b.line.key]) continue;
          if (a.itemId === b.itemId) continue;
          var bCovers = (DOWNLOADS[b.itemId] || {}).covers || [];
          // Find overlap (any item id in both covers lists, excluding self).
          var overlap = aCovers.filter(function (x) {
            return x !== a.itemId && x !== b.itemId && bCovers.indexOf(x) > -1;
          });
          if (overlap.length > 0) {
            // Remove the one added LATER (higher idx in cart) so the user
            // keeps their original choice. If indices are equal somehow,
            // remove the smaller one (fewer items covered).
            var loser, winner;
            if (b.idx > a.idx) { loser = b; winner = a; }
            else if (a.idx > b.idx) { loser = a; winner = b; }
            else if (bCovers.length < aCovers.length) { loser = b; winner = a; }
            else { loser = a; winner = b; }
            seenKeys[loser.line.key] = true;
            removals.push({
              key: loser.line.key,
              reason: 'overlapping-bundle',
              itemId: loser.itemId,
              because: winner.itemId,
              sessionId: sid
            });
          }
        }
      }

      // Rule 3: Higher-tier print supersedes lower-tier print at SAME QUANTITY
      // (Different quantities = user wants different runs, leave alone.)
      var byQty = {};
      prints.forEach(function (p) {
        var q = PRINT_QTY[p.itemId];
        if (!byQty[q]) byQty[q] = [];
        byQty[q].push(p);
      });
      Object.keys(byQty).forEach(function (q) {
        var ps = byQty[q];
        var hasFb = ps.some(function (p) { return PRINT_SIDES[p.itemId] === 'fb'; });
        if (hasFb) {
          ps.forEach(function (p) {
            if (PRINT_SIDES[p.itemId] === 'f' && !seenKeys[p.line.key]) {
              seenKeys[p.line.key] = true;
              removals.push({
                key: p.line.key,
                reason: 'superseded-by-larger-print',
                itemId: p.itemId,
                because: ps.find(function (x) { return PRINT_SIDES[x.itemId] === 'fb'; }).itemId,
                sessionId: sid
              });
            }
          });
        }
      });
    });

    // Strip any legacy rush variants from R13/R20 (the old per-design /
    // per-tier rush variants). These SKUs no longer exist in the new
    // architecture — rush is one cart-wide line pair (base + per-sticker).
    (cart.items || []).forEach(function (it) {
      var legacyByVariant = LEGACY_RUSH_VARIANTS[it.variant_id];
      var legacyByItemId  = ((it.properties || {})._item_id === 'rush_production');
      if (!(legacyByVariant || legacyByItemId)) return;
      if (seenKeys[it.key]) return;
      seenKeys[it.key] = true;
      removals.push({
        key: it.key,
        reason: 'rush-legacy',
        itemId: (it.properties || {})._item_id || ('legacy-rush-' + it.variant_id),
        because: 'rush is now a single cart-wide toggle on the cart page'
      });
    });

    // Rush production sync — cart-wide toggle.
    var rushPlan = planRushSync(cart, seenKeys);
    rushPlan.removals.forEach(function (r) { removals.push(r); });
    rushPlan.additions.forEach(function (a) { additions.push(a); });

    return {
      removals: removals,
      additions: additions,
      qtyChanges: rushPlan.qtyChanges
    };
  }

  // -------------------------------------------------------------------------
  // Rush sync — keeps the rush_base_fee + rush_per_sticker lines in lockstep
  // with the customer's toggle and the cart's total sticker count.
  //
  // Rules:
  //   ATTR=on AND total_stickers > 0:
  //     base line should exist with qty 1
  //     per-sticker line should have qty = max(0, total_stickers − 32);
  //       qty 0 = remove the per-sticker line entirely (Shopify minimum is 1)
  //   Otherwise (toggle off, missing, or no print items in cart):
  //     both rush lines should be removed
  //
  // Behaviour: returns { removals, additions, qtyChanges }. Caller applies in
  // order: removals first, then qtyChanges, then additions.
  // -------------------------------------------------------------------------
  function planRushSync(cart, seenKeys) {
    var out = { removals: [], additions: [], qtyChanges: [] };
    var attrs = (cart && cart.attributes) || {};
    var rushOn = (attrs.rush_production === 'on' || attrs.rush_production === true);

    // Total stickers across all print lines in the cart (rush is cart-wide,
    // not per-design).
    var totalStickers = 0;
    (cart.items || []).forEach(function (it) {
      var iid = itemIdOf(it);
      var per = STICKERS_PER_SKU[iid];
      if (per) totalStickers += per * (it.quantity || 0);
    });

    // Find existing rush lines (matched by variant_id, not item_id, so we
    // catch them even if the line was added without the _item_id property).
    var baseLine = null;
    var perLine = null;
    (cart.items || []).forEach(function (it) {
      var which = RUSH_VARIANT_IDS[it.variant_id];
      if (which === 'base')       baseLine = it;
      if (which === 'perSticker') perLine  = it;
    });

    var wantBase = rushOn && totalStickers > 0;
    var wantPerQty = wantBase ? Math.max(0, totalStickers - 32) : 0;

    // ----- BASE LINE -----
    if (!wantBase) {
      if (baseLine && !seenKeys[baseLine.key]) {
        seenKeys[baseLine.key] = true;
        out.removals.push({
          key: baseLine.key,
          reason: 'rush-disabled',
          itemId: 'rush_base_fee',
          because: rushOn ? 'no print items in cart' : 'rush toggle off'
        });
      }
    } else {
      if (!baseLine) {
        out.additions.push({
          variantId: RUSH.base.variantId,
          itemId: 'rush_base_fee',
          quantity: 1,
          properties: { _item_id: 'rush_base_fee', _addon: 'rush' },
          because: 'rush-on'
        });
      } else if ((baseLine.quantity || 0) !== 1 && !seenKeys[baseLine.key]) {
        out.qtyChanges.push({ key: baseLine.key, quantity: 1, itemId: 'rush_base_fee' });
      }
    }

    // ----- PER-STICKER LINE -----
    if (wantPerQty <= 0) {
      if (perLine && !seenKeys[perLine.key]) {
        seenKeys[perLine.key] = true;
        out.removals.push({
          key: perLine.key,
          reason: 'rush-disabled',
          itemId: 'rush_per_sticker',
          because: !wantBase ? (rushOn ? 'no print items in cart' : 'rush toggle off')
                              : 'cart at base tier (≤32 stickers)'
        });
      }
    } else {
      if (!perLine) {
        out.additions.push({
          variantId: RUSH.perSticker.variantId,
          itemId: 'rush_per_sticker',
          quantity: wantPerQty,
          properties: { _item_id: 'rush_per_sticker', _addon: 'rush', _stickers: String(totalStickers) },
          because: 'rush-on'
        });
      } else if ((perLine.quantity || 0) !== wantPerQty && !seenKeys[perLine.key]) {
        out.qtyChanges.push({ key: perLine.key, quantity: wantPerQty, itemId: 'rush_per_sticker' });
      }
    }

    return out;
  }

  // Plan quantity caps (download lines should never be qty > 1).
  function planQtyCaps(cart) {
    var caps = [];
    (cart.items || []).forEach(function (line) {
      var id = itemIdOf(line);
      if (isDownload(id) && line.quantity > 1) {
        caps.push({ key: line.key, quantity: 1, itemId: id });
      }
    });
    return caps;
  }

  // -------------------------------------------------------------------------
  // Banner UI
  // -------------------------------------------------------------------------
  function ensureBanner() {
    var b = document.getElementById('pf-cart-rules-banner');
    if (b) return b;
    b = document.createElement('div');
    b.id = 'pf-cart-rules-banner';
    b.style.cssText = [
      'position:fixed', 'top:0', 'left:0', 'right:0', 'z-index:99999',
      'background:#FF5500', 'color:#fff', 'padding:14px 18px',
      'font-family:"Barlow Condensed", Arial, sans-serif', 'font-weight:700',
      'font-size:15px', 'letter-spacing:0.3px',
      'box-shadow:0 4px 12px rgba(0,0,0,0.25)',
      'transform:translateY(-110%)', 'transition:transform 0.35s ease',
      'display:flex', 'align-items:center', 'gap:12px'
    ].join(';');
    var msg = document.createElement('div');
    msg.id = 'pf-cart-rules-banner-msg';
    msg.style.cssText = 'flex:1;line-height:1.35;';
    var close = document.createElement('button');
    close.textContent = '✕';
    close.setAttribute('aria-label', 'Dismiss');
    close.style.cssText = 'background:transparent;border:none;color:#fff;font-size:18px;cursor:pointer;padding:4px 8px;';
    close.onclick = function () { b.style.transform = 'translateY(-110%)'; };
    b.appendChild(msg);
    b.appendChild(close);
    document.body.appendChild(b);
    return b;
  }

  function showBanner(htmlMessage, opts) {
    opts = opts || {};
    var b = ensureBanner();
    document.getElementById('pf-cart-rules-banner-msg').innerHTML = htmlMessage;
    requestAnimationFrame(function () { b.style.transform = 'translateY(0)'; });
    if (opts.autoHideMs !== 0) {
      clearTimeout(b._hideT);
      b._hideT = setTimeout(function () { b.style.transform = 'translateY(-110%)'; }, opts.autoHideMs || 7000);
    }
  }

  function bannerForRemovals(removals, additions) {
    if (!removals.length && (!additions || !additions.length)) return '';
    var hasFreeWithPrint = removals.some(function (r) { return r.reason === 'included-with-print'; });
    var hasFreeWithPromo = removals.some(function (r) { return r.reason === 'included-with-promo'; });
    var hasBundle       = removals.some(function (r) { return r.reason === 'covered-by-bundle'; });
    var hasPrintUpgrade = removals.some(function (r) { return r.reason === 'superseded-by-larger-print'; });
    var hasOverlap      = removals.some(function (r) { return r.reason === 'overlapping-bundle'; });
    var hasPartial      = removals.some(function (r) { return r.reason === 'partial-cover-by-print'; });
    var msgs = [];
    if (hasFreeWithPrint) {
      var n = removals.filter(function (r) { return r.reason === 'included-with-print'; }).length;
      msgs.push('🎁 ' + (n === 1 ? 'A download was' : n + ' downloads were') +
                ' removed — your design files are <strong>included free</strong> with your print order.');
    }
    if (hasFreeWithPromo) {
      var nP = removals.filter(function (r) { return r.reason === 'included-with-promo'; }).length;
      msgs.push('🎁 ' + (nP === 1 ? 'A download was' : nP + ' downloads were') +
                ' removed — your <strong>promo package</strong> already includes the bag\'s design files free.');
    }
    if (hasBundle) {
      msgs.push('📦 An individual download was removed — it is already part of the bundle in your cart.');
    }
    if (hasPrintUpgrade) {
      msgs.push('⬆️ A smaller print order was removed — the larger Front+Back order at the same quantity covers it.');
    }
    if (hasOverlap) {
      msgs.push('🔄 Two overlapping download bundles were trimmed — you would have paid twice for the same files.');
    }
    if (hasPartial) {
      var added = (additions && additions.length) ? additions.length : 0;
      var addedNote = added > 0
        ? ' We added the <strong>back-only download</strong> in its place so you keep what you needed.'
        : '';
      msgs.push('💰 A download bundle was swapped — your print already includes the front file free.' + addedNote);
    }
    return msgs.join('<br>');
  }

  // -------------------------------------------------------------------------
  // Cart API helpers
  // -------------------------------------------------------------------------
  // _internalMutation: while true, our fetch hook (further down) skips its
  // normalize() trigger. Set inside normalize() during the mutation chain so
  // each internal addLine / changeQty doesn't recursively re-fire normalize.
  // External callers (rush toggle click, design app cart adds, etc.) leave
  // it false so their mutations DO trigger normalize as designed.
  // Jacques 2026-05-09.
  var _internalMutation = false;

  // Cached last-known rush state (Jacques 2026-05-23). Used so a re-mount
  // of mountRushToggle() paints the toggle correctly SYNCHRONOUSLY before
  // the async getCart() in refreshRushToggle() resolves — prevents the
  // “off for one frame” flicker that landed on /cart after softRefresh.
  var _lastRushOn = null;
  function fetchJSON(url, opts) {
    opts = opts || {};
    opts.credentials = 'same-origin';
    opts.headers = Object.assign({ 'Accept': 'application/json', 'Content-Type': 'application/json' }, opts.headers || {});
    return fetch(url, opts).then(function (r) { return r.json(); });
  }
  function getCart() { return fetchJSON('/cart.js'); }
  function changeQty(key, qty) {
    return fetchJSON('/cart/change.js', { method: 'POST', body: JSON.stringify({ id: key, quantity: qty }) });
  }
  function addLine(variantId, properties, quantity) {
    return fetchJSON('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify({
        id: variantId,
        quantity: (typeof quantity === 'number' && quantity > 0) ? quantity : 1,
        properties: properties || {}
      })
    });
  }
  // Add multiple lines in a single /cart/add.js call. Avoids the visual
  // jump that happens when Shopify re-renders cart sections after each
  // individual add. Used by the rush toggle which adds 2 lines (base + per-sticker).
  function addLines(items) {
    return fetchJSON('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify({ items: items.map(function (a) {
        return {
          id: a.variantId,
          quantity: (typeof a.quantity === 'number' && a.quantity > 0) ? a.quantity : 1,
          properties: a.properties || {}
        };
      }) })
    });
  }
  function updateAttribute(name, value) {
    var body = { attributes: {} };
    body.attributes[name] = value;
    return fetchJSON('/cart/update.js', { method: 'POST', body: JSON.stringify(body) });
  }

  // -------------------------------------------------------------------------
  // Normalize the cart. Returns the list of removals applied.
  // -------------------------------------------------------------------------
  // Concurrency: if a mutation arrives while normalize is running, set a
  // pending flag so we re-run after current pass finishes. Prevents the
  // cart from sitting in a dirty state when two add-to-cart calls land
  // back-to-back.
  var _running = false;
  var _pending = false;
  function normalize() {
    if (_running) {
      _pending = true;
      return Promise.resolve({ removals: [], caps: [], deferred: true });
    }
    _running = true;
    _internalMutation = true; // suppress recursive normalize triggers from our own mutations
    return getCart().then(function (cart) {
      var plan = planRemovals(cart);
      var removals, additions, qtyChanges;
      if (Array.isArray(plan)) { removals = plan; additions = []; qtyChanges = []; }
      else {
        removals = plan.removals || [];
        additions = plan.additions || [];
        qtyChanges = plan.qtyChanges || [];
      }
      var caps = planQtyCaps(cart);
      // Merge rush-driven qty changes with cap-driven qty changes. Rush
      // changes take precedence if both target the same key (rush quantity
      // is exact, caps just clamp downloads to 1 — they shouldn't ever
      // collide, but better safe than sorry).
      var qtyByKey = {};
      caps.forEach(function (c) { qtyByKey[c.key] = c.quantity; });
      qtyChanges.forEach(function (c) { qtyByKey[c.key] = c.quantity; });
      var allQtyChanges = Object.keys(qtyByKey).map(function (k) {
        return { key: k, quantity: qtyByKey[k] };
      });

      if (!removals.length && !allQtyChanges.length && !additions.length) {
        _running = false;
        _internalMutation = false;
        return { removals: [], caps: [], additions: [] };
      }
      // Apply sequentially so Shopify doesn't get confused. Bundle all
      // additions into a single /cart/add.js {items: [...]} call so Shopify
      // only re-renders the cart sections ONCE at the end (not per-line).
      // Otherwise on /cart, two adds back-to-back cause two separate cart
      // re-renders — visible as guttering / jumping.
      var chain = Promise.resolve();
      removals.forEach(function (r) {
        chain = chain.then(function () { return changeQty(r.key, 0); });
      });
      allQtyChanges.forEach(function (c) {
        chain = chain.then(function () { return changeQty(c.key, c.quantity); });
      });
      if (additions.length === 1) {
        var a0 = additions[0];
        chain = chain.then(function () {
          return addLine(a0.variantId, a0.properties, a0.quantity).catch(function (e) {
            console.warn('[pf-cart-rules] auto-add failed for', a0.itemId, e);
          });
        });
      } else if (additions.length > 1) {
        chain = chain.then(function () {
          return addLines(additions).catch(function (e) {
            // If the bulk add fails (e.g. one item rejected), fall back to
            // sequential adds so partial success still works.
            console.warn('[pf-cart-rules] bulk-add failed, falling back to sequential', e);
            var seq = Promise.resolve();
            additions.forEach(function (a) {
              seq = seq.then(function () {
                return addLine(a.variantId, a.properties, a.quantity).catch(function (ee) {
                  console.warn('[pf-cart-rules] seq auto-add failed for', a.itemId, ee);
                });
              });
            });
            return seq;
          });
        });
      }
      return chain.then(function () {
        _running = false;
        _internalMutation = false;
        if (removals.length || additions.length) {
          var html = bannerForRemovals(removals, additions);
          if (html) showBanner(html);
        }
        // Tell other cart listeners (slidecart, drawer) to refresh
        document.dispatchEvent(new CustomEvent('pf:cart:normalized', { detail: { removals: removals, caps: caps } }));
        // Best-effort: ping native cart drawer/section refreshes
        if (typeof window.refreshCart === 'function') { try { window.refreshCart(); } catch (_) {} }
        // On /cart: refresh the cart sections via Shopify's Section Rendering
        // API instead of doing a hard page reload. The hard reload was
        // causing visible "guttering / jumping" on the rush toggle because
        // it kicked in 1.2s after the toggle UI had already animated.
        // Soft refresh keeps scroll position and the toggle card stays put.
        if (location.pathname === '/cart' && (removals.length || additions.length || allQtyChanges.length)) {
          softRefreshCartPage();
        }
        // Refresh the rush toggle's price preview now that lines settled.
        if (typeof refreshRushToggle === 'function') {
          try { refreshRushToggle(); } catch (_) {}
        }
        // If a mutation arrived while we were running, re-run to catch any
        // contradictions introduced by it.
        if (_pending) {
          _pending = false;
          setTimeout(normalize, 100);
        }
        return { removals: removals, caps: caps };
      });
    }).catch(function (e) {
      _running = false;
      _internalMutation = false;
      var hadPending = _pending;
      _pending = false;
      console.warn('[pf-cart-rules] normalize failed', e);
      if (hadPending) setTimeout(normalize, 200);
      return { removals: [], caps: [] };
    });
  }

  window.pfCartNormalize = normalize;

  // -------------------------------------------------------------------------
  // Hook into /cart/add.js, /cart/change.js, /cart/update.js
  // We patch fetch + XHR. We run normalize() AFTER any successful mutation.
  // -------------------------------------------------------------------------
  var MUTATION_RE = /\/cart\/(add|change|update)(\.js|\.json)?(\?|$)/;

  // fetch hook — triggers normalize() after any /cart/{add,change,update}.
  // BUT: skips the trigger if the mutation came from inside this script
  // (_internalMutation flag) so normalize doesn't recurse on its own work.
  if (window.fetch) {
    var _fetch = window.fetch;
    window.fetch = function (input, init) {
      var url = (typeof input === 'string') ? input : (input && input.url) || '';
      var wasInternal = _internalMutation;
      var p = _fetch.apply(this, arguments);
      if (MUTATION_RE.test(url) && !wasInternal) {
        p.then(function (resp) {
          if (resp && resp.ok) {
            // Defer slightly so Shopify finishes its own internal updates first
            setTimeout(normalize, 80);
          }
        }).catch(function () {});
      }
      return p;
    };
  }

  // XHR hook — same internal-mutation guard.
  (function () {
    var _open = XMLHttpRequest.prototype.open;
    var _send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this._pfUrl = url || '';
      this._pfInternal = _internalMutation;
      return _open.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      var x = this;
      var url = x._pfUrl || '';
      if (MUTATION_RE.test(url) && !x._pfInternal) {
        x.addEventListener('load', function () {
          if (x.status >= 200 && x.status < 300) setTimeout(normalize, 80);
        });
      }
      return _send.apply(this, arguments);
    };
  })();

  // -------------------------------------------------------------------------
  // Soft cart refresh — re-fetches the cart's sections via Shopify's Section
  // Rendering API and swaps them in-place. No full page reload, no scroll
  // jump. Used in place of location.reload() on the /cart page after
  // normalize() applies removals/additions. Falls back to reload only if
  // the section API isn't usable for some reason.
  // -------------------------------------------------------------------------
  function softRefreshCartPage() {
    // Find every Shopify section element on the page so we can ask the
    // Section Rendering API for fresh HTML for each one. The cart page
    // typically has 1–3 sections (cart-items, cart-footer, etc.).
    var sectionEls = document.querySelectorAll('[id^="shopify-section-"]');
    if (!sectionEls.length) {
      // Nothing to soft-refresh — the theme isn't using Sections, fall back.
      // (Don't reload — that's what we're trying to avoid. The cart drawer
      // refresh hooks above will pick up the rest.)
      return;
    }
    var sectionIds = [];
    sectionEls.forEach(function (el) {
      sectionIds.push(el.id.replace(/^shopify-section-/, ''));
    });
    // Rush-toggle preservation (Jacques 2026-05-23): the rush card lives
    // inside the cart-items section, so the innerHTML swap below would nuke
    // it — causing a visible flicker where the toggle drops out, then re-
    // appears one /cart.js fetch later in its default unchecked state before
    // refreshRushToggle() catches up. Detach the rush card to <body> BEFORE
    // the swap and stash a placeholder pin where it lived. After the swap we
    // re-insert the same DOM node at (or as close as possible to) its
    // original position. This keeps the checked state, the painted slider,
    // and the live price preview rock-solid across the re-render.
    var rushCardEl = document.getElementById('pf-rush-card');
    var rushPlaceholder = null;
    if (rushCardEl && rushCardEl.parentNode) {
      rushPlaceholder = document.createComment('pf-rush-card-pin');
      rushCardEl.parentNode.insertBefore(rushPlaceholder, rushCardEl);
      // Park it on <body> so the section swap can't reach it.
      document.body.appendChild(rushCardEl);
    }
    // Fetch /cart with sections= query — Shopify returns JSON with rendered
    // HTML for each section.
    var url = '/cart?sections=' + encodeURIComponent(sectionIds.join(','));
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        sectionIds.forEach(function (id) {
          var html = data && data[id];
          if (!html) return;
          var existing = document.getElementById('shopify-section-' + id);
          if (!existing) return;
          // Parse the returned HTML and swap in just the inner contents to
          // avoid breaking the wrapping element's id/attrs.
          var tmp = document.createElement('div');
          tmp.innerHTML = html;
          var fresh = tmp.querySelector('#shopify-section-' + id);
          if (fresh) {
            existing.innerHTML = fresh.innerHTML;
          } else {
            existing.innerHTML = html;
          }
        });
        // Re-attach the rush card. If the placeholder pin survived (its
        // parent section may have been replaced, in which case the comment
        // node was discarded with the rest of the innerHTML), drop it back
        // at the pin location; otherwise let mountRushToggle find a fresh
        // anchor and re-insert it.
        if (rushCardEl) {
          var pinParent = rushPlaceholder && rushPlaceholder.parentNode;
          if (pinParent) {
            pinParent.insertBefore(rushCardEl, rushPlaceholder);
            pinParent.removeChild(rushPlaceholder);
          } else if (rushCardEl.parentNode === document.body) {
            // The pin got eaten by the swap. Move the card back into the
            // cart area via the same anchor mountRushToggle uses, but DO NOT
            // recreate the node (preserve its state).
            var anchor =
              document.querySelector('[name="checkout"]') ||
              document.querySelector('button[type="submit"][name="checkout"]') ||
              document.querySelector('.cart__checkout-button') ||
              document.querySelector('.cart__ctas') ||
              document.querySelector('cart-items') ||
              document.querySelector('form[action*="/cart"]');
            if (anchor && anchor.parentNode) {
              anchor.parentNode.insertBefore(rushCardEl, anchor);
            }
          }
          // Refresh price preview / sticker count from the now-current cart.
          if (typeof refreshRushToggle === 'function') {
            try { refreshRushToggle(); } catch (_) {}
          }
        } else {
          // No card was mounted before — do a fresh mount. This path runs
          // post section-refresh so the anchor SHOULD exist; pass true so
          // we body-append if not (better visible-but-misplaced than gone).
          if (typeof mountRushToggle === 'function') {
            try { mountRushToggle(true); } catch (_) {}
          }
        }
      })
      .catch(function () {
        // If section refresh failed, leave the page as-is. The toggle's
        // refreshRushToggle() will still update the price preview from
        // /cart.js, and Shopify's own line-item displays usually update
        // themselves after /cart/add.js anyway. Re-attach the rush card if
        // we detached it.
        if (rushCardEl && rushCardEl.parentNode === document.body) {
          var anchor2 =
            document.querySelector('[name="checkout"]') ||
            document.querySelector('button[type="submit"][name="checkout"]') ||
            document.querySelector('.cart__checkout-button') ||
            document.querySelector('.cart__ctas') ||
            document.querySelector('cart-items') ||
            document.querySelector('form[action*="/cart"]');
          if (anchor2 && anchor2.parentNode) {
            anchor2.parentNode.insertBefore(rushCardEl, anchor2);
          }
        }
        if (rushPlaceholder && rushPlaceholder.parentNode) {
          rushPlaceholder.parentNode.removeChild(rushPlaceholder);
        }
      });
  }

  // On page load on the /cart page, run a normalization pass to clean up any
  // pre-existing contradictions (e.g. customer added items days ago, before this rolled out).
  function onReady(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }
  // _pfDebug — enable verbose console logging by appending ?_pfdebug=1
  // to the URL or by setting window._pfDebug=true in DevTools before
  // reload. Used to verify cart:update / mount / observer behaviour.
  try {
    if (location.search.indexOf('_pfdebug=1') !== -1 ||
        (typeof localStorage !== 'undefined' && localStorage.getItem('_pfdebug') === '1')) {
      window._pfDebug = true;
    }
  } catch(_){}

  onReady(function () {
    if (isCartPage()) {
      normalize();
      // Initial mount: allow body fallback because by the time DOMContent-
      // Loaded fires, the cart section has been server-side-rendered.
      mountRushToggle(true);
    }
    // Split-delivery banner shows on cart page AND cart drawer (any page).
    mountSplitDeliveryBanner();
  });

  // Re-mount/refresh the toggle on cart-page reloads triggered by normalize()
  // (Shopify often reloads the cart page after a mutation — our card persists
  // because mountRushToggle attaches in onReady, but we also re-render after
  // normalize finishes so the price preview stays accurate.)
  document.addEventListener('pf:cart:normalized', function () {
    if (location.pathname === '/cart') refreshRushToggle();
    // Re-mount/refresh the split-delivery banner on every cart mutation
    // (cart-page section refresh may have rebuilt the DOM and dropped it).
    mountSplitDeliveryBanner();
  });

  // -------------------------------------------------------------------------
  // 2026-05-23 REDO — Horizon native cart:update listener.
  //
  // Bug: removing a line item on /cart made the rush toggle disappear until
  // a manual page refresh. Root cause: Horizon's <cart-items-component>
  // handles line-item-remove by POSTing /cart/change with sections=, then
  // calling morphSection() to swap the cart section's DOM. The rush card
  // (#pf-rush-card) lives INSIDE that section (mounted above the checkout
  // button), so morphSection destroys it. Our softRefreshCartPage() detach-
  // and-reattach pattern handled OUR code path (normalize()), but NOT the
  // native Horizon path — which dispatches a 'cart:update' event
  // (ThemeEvents.cartUpdate = 'cart:update') that bubbles on document.
  //
  // Fix: listen for cart:update on document. The card may have been wiped
  // by morphSection (sync, runs before our handler) — so we always go
  // through mountRushToggle() which re-creates from scratch if missing,
  // or refreshes in place if it survived. _lastRushOn pre-seed keeps the
  // toggle state correct from the first paint, no flicker.
  // -------------------------------------------------------------------------
  function isCartPage(){
    var p = location.pathname || '';
    return p === '/cart' || p === '/cart/' || p.indexOf('/cart?') === 0 || p.indexOf('/cart#') === 0;
  }
  // 2026-05-23 REDO2-mobile (Jacques re-reported on iPhone Safari):
  //   Previous fix attached a cart:update listener + double-rAF re-mount,
  //   but mountRushToggle() insertBefore-d the card relative to the
  //   [name="checkout"] anchor. If the anchor wasn't in the DOM yet (Horizon
  //   sometimes lazy-hydrates the checkout button via <is-land>), the card
  //   fell through to body.appendChild which puts it OFF-SCREEN below the
  //   cart footer. To the user this looks indistinguishable from "the
  //   toggle disappeared".
  //
  //   Fix: scheduleRushMount() does a small retry burst (rAF + 100ms +
  //   500ms + 1500ms) so we always land the card next to a fresh anchor
  //   once Horizon's section morph + island hydration are both complete.
  //   Idempotent: mountRushToggle() short-circuits if the card already
  //   exists. The mount itself is also tightened to PREFER the same
  //   anchor location as the initial mount (above [name="checkout"]) and
  //   only fall back to body.appendChild on the final retry.
  function scheduleRushMount(reason){
    if (!isCartPage()) return;
    var attempt = 0;
    var attemptOnce = function(isFinal){
      try {
        // Re-check existence each attempt — cart-update from drawer-side
        // can fire on /cart too and we don't want to double-mount.
        if (document.getElementById('pf-rush-card')) {
          try { refreshRushToggle(); } catch(_){}
        } else {
          try { mountRushToggle(isFinal === true); } catch(_){}
        }
        try { refreshRushToggle(); } catch(_){}
        try { mountSplitDeliveryBanner(); } catch(_){}
      } catch(_){}
      if (window._pfDebug) {
        try { console.log('[pf-cart-rules] rush mount attempt', attempt, reason, 'card?', !!document.getElementById('pf-rush-card')); } catch(_){}
      }
    };
    // Burst: immediate + 2 rAF + 100ms + 500ms + 1500ms. Cheap because
    // mountRushToggle short-circuits when card already exists.
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(function(){ attempt = 1; attemptOnce(false); requestAnimationFrame(function(){ attempt = 2; attemptOnce(false); }); });
    } else {
      attempt = 1; attemptOnce(false);
    }
    setTimeout(function(){ attempt = 3; attemptOnce(false); }, 100);
    setTimeout(function(){ attempt = 4; attemptOnce(false); }, 500);
    setTimeout(function(){ attempt = 5; attemptOnce(true); }, 1500);
  }

  document.addEventListener('cart:update', function (e) {
    if (!isCartPage()) return;
    scheduleRushMount('cart:update');
  }, false);

  // Belt-and-brace: also listen on bubbled DiscountUpdateEvent — in some
  // flows the cart section is re-rendered via sectionRenderer.renderSection
  // (no cart:update) but the discount event is fired.
  document.addEventListener('discount:update', function () {
    if (!isCartPage()) return;
    scheduleRushMount('discount:update');
  }, false);

  // ALSO listen on the cart-items-component element directly. In some
  // Horizon code paths the event is dispatched on the component (which
  // bubbles to document) but a few framework code paths
  // (DiscountUpdateEvent in particular, and any handler that hops through
  // <is-land>) can prevent the bubble from reaching document depending on
  // how stopPropagation is used downstream. Listening on the component
  // itself catches both. Idempotent re-attach is safe — even if the
  // element gets replaced by morphSection, the document-level listener
  // above still fires.
  function attachComponentListener(){
    var c = document.querySelector('cart-items-component');
    if (!c) return;
    if (c._pfRushListenerAttached) return;
    c._pfRushListenerAttached = true;
    c.addEventListener('cart:update', function(){ scheduleRushMount('component:cart:update'); }, false);
    c.addEventListener('discount:update', function(){ scheduleRushMount('component:discount:update'); }, false);
  }
  onReady(attachComponentListener);

  // MutationObserver fallback — catches any DOM mutation mechanism we
  // didn't predict (innerHTML reset, custom-element disconnect/reconnect,
  // Liquid section rerender that skips the events entirely). Only runs on
  // /cart so it's cheap elsewhere.
  function installRushCardObserver(){
    if (window._pfRushObserverInstalled) return;
    if (!isCartPage()) return;
    if (typeof MutationObserver !== 'function') return;
    window._pfRushObserverInstalled = true;
    var checkPending = false;
    var lastSeen = !!document.getElementById('pf-rush-card');
    var scheduleCheck = function(){
      if (checkPending) return;
      checkPending = true;
      requestAnimationFrame(function(){
        checkPending = false;
        var nowSeen = !!document.getElementById('pf-rush-card');
        // Only act on a transition from seen->gone (re-mount). If still
        // missing AND no print items, refreshRushToggle will keep it
        // hidden anyway, so this is also cheap.
        if (!nowSeen) {
          // The cart page WAS rendered (we're here from a mutation event
          // on body, so DOM is alive). Re-mount.
          try { mountRushToggle(false); } catch(_){}
          try { refreshRushToggle(); } catch(_){}
        }
        lastSeen = !!document.getElementById('pf-rush-card');
      });
    };
    var obs = new MutationObserver(function(mutations){
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.addedNodes.length || m.removedNodes.length) {
          // Cheap heuristic: only react if the mutation touched the cart
          // area. Walk up from target looking for a cart-related ancestor.
          var t = m.target;
          var depth = 0;
          while (t && depth < 6) {
            var id = (t.id || '');
            var tag = (t.tagName || '').toLowerCase();
            if (tag === 'cart-items-component' || tag === 'cart-drawer-component' ||
                id.indexOf('cart') !== -1 || id.indexOf('shopify-section-cart') !== -1 ||
                id === 'main' || tag === 'main' || tag === 'body') {
              scheduleCheck();
              return;
            }
            t = t.parentNode; depth++;
          }
        }
      }
    });
    // Observe body — cheap because we filter mutations by ancestor walk above.
    // We tried scoping to the cart-section ID but it isn't reliably present
    // before the section renders (see 2026-05-23 REDO2-mobile diagnosis).
    obs.observe(document.body, { childList: true, subtree: true });
  }
  onReady(installRushCardObserver);

  // -------------------------------------------------------------------------
  // Rush toggle UI — cart page only.
  //
  // Renders a single "⚡ Add Rush Production" card above the checkout button.
  // Toggle persists in cart.attributes.rush_production = 'on'|''. The card
  // shows the live computed price ($50 + $0.25 × max(0, stickers − 32)).
  // The actual cart line sync is handled by planRushSync() during normalize().
  // -------------------------------------------------------------------------
  // mountRushToggle(allowBodyFallback)
  //   allowBodyFallback (default false): when the [name="checkout"] anchor
  //     can't be found, traditionally we appended to document.body so the
  //     card was at least in the DOM. But that produces an off-screen card
  //     for the user (looks indistinguishable from "toggle disappeared").
  //     scheduleRushMount() now retries multiple times — only the FINAL
  //     retry passes allowBodyFallback=true, so intermediate attempts
  //     simply bail and wait for the anchor to hydrate. This fixes the
  //     2026-05-23 mobile-Safari report where the toggle vanished after
  //     line removal until full page refresh.
  function mountRushToggle(allowBodyFallback) {
    if (document.getElementById('pf-rush-card')) {
      refreshRushToggle();
      return;
    }
    var card = document.createElement('div');
    card.id = 'pf-rush-card';
    // Pre-seed the initial "hidden until we know there are print items"
    // state so we never flash an empty/off card on a cart that doesn't
    // qualify for rush. refreshRushToggle() will reveal+paint it correctly.
    var _preSeedOn = (_lastRushOn === true);
    card.style.cssText = [
      'margin:18px auto', 'max-width:720px',
      'background:#fff8ec', 'border:2px solid #FF5500', 'border-radius:14px',
      'padding:18px 20px', 'font-family:"Barlow Condensed",Arial,sans-serif',
      'box-shadow:0 4px 14px rgba(255,85,0,0.12)',
      'display:flex', 'gap:14px', 'align-items:center',
      'transition:opacity 0.3s ease'
    ].join(';');
    card.innerHTML =
      '<div style="flex:1;">' +
        '<div style="font-size:18px;font-weight:800;color:#1a1a1a;letter-spacing:0.4px;margin-bottom:4px;">' +
          '⚡ <span id="pf-rush-headline">Add Rush Production</span>' +
        '</div>' +
        '<div style="font-size:13px;color:#444;line-height:1.45;">' +
          '<span id="pf-rush-sub">Ships in <strong>3–5 business days</strong> instead of 7–10. Pricing scales with order size.</span>' +
        '</div>' +
        '<div id="pf-rush-price" style="font-size:13px;color:#666;margin-top:6px;"></div>' +
      '</div>' +
      '<label id="pf-rush-switch-wrap" style="position:relative;display:inline-block;width:52px;height:30px;flex-shrink:0;cursor:pointer;">' +
        '<input id="pf-rush-switch" type="checkbox" style="opacity:0;width:0;height:0;">' +
        '<span id="pf-rush-slider" style="position:absolute;inset:0;background:#ccc;border-radius:30px;transition:0.25s;">' +
          '<span id="pf-rush-knob" style="position:absolute;left:3px;top:3px;width:24px;height:24px;background:#fff;border-radius:50%;transition:0.25s;box-shadow:0 1px 4px rgba(0,0,0,0.25);"></span>' +
        '</span>' +
      '</label>';

    // Find the checkout button on the cart page and insert above it.
    // Try multiple selectors used by Shopify themes (Dawn, Horizon, Studio, etc.)
    var anchor =
      document.querySelector('[name="checkout"]') ||
      document.querySelector('button[type="submit"][name="checkout"]') ||
      document.querySelector('.cart__checkout-button') ||
      document.querySelector('.cart__ctas') ||
      document.querySelector('cart-items') ||
      document.querySelector('form[action*="/cart"]');
    if (anchor && anchor.parentNode) {
      anchor.parentNode.insertBefore(card, anchor);
    } else if (allowBodyFallback === true) {
      // Last resort (only after retries exhausted): append to body. This
      // ensures the card exists somewhere in the DOM even if Horizon never
      // hydrated the checkout button. May render off-screen.
      document.body.appendChild(card);
      if (window._pfDebug) {
        try { console.warn('[pf-cart-rules] mountRushToggle: no anchor found, using body fallback'); } catch(_){}
      }
    } else {
      // Intermediate attempt — don't mount in a bad location. Caller
      // (scheduleRushMount) will retry shortly when the anchor hydrates.
      if (window._pfDebug) {
        try { console.log('[pf-cart-rules] mountRushToggle: no anchor yet, skipping (will retry)'); } catch(_){}
      }
      return;
    }

    var checkbox = document.getElementById('pf-rush-switch');
    // Seed the just-created checkbox + slider with our cached state so the
    // first paint after a section-swap re-mount matches what the user just
    // toggled. refreshRushToggle() will reconcile against /cart.js shortly.
    if (checkbox && _preSeedOn) checkbox.checked = true;
    paintToggle(_preSeedOn);
    checkbox.addEventListener('change', function () {
      var on = checkbox.checked;
      // Cache for any subsequent re-mount (section swap, drawer rebuild).
      _lastRushOn = on;
      // Optimistically update the visual state.
      paintToggle(on);
      // Disable the switch briefly while we update the cart attribute, to
      // prevent rapid double-clicks from racing with normalize().
      checkbox.disabled = true;
      // Suppress the fetch hook's deferred normalize trigger — we call
      // normalize() ourselves below, AFTER the attribute change has
      // propagated. Without this, the fetch hook could fire normalize()
      // before the attribute write hit the server's cart state, causing
      // planRushSync to read "toggle off" and do nothing.
      _internalMutation = true;
      updateAttribute('rush_production', on ? 'on' : '').then(function () {
        _internalMutation = false;
        return normalize();
      }).then(function () {
        // normalize() also calls refreshRushToggle() + softRefreshCartPage().
        checkbox.disabled = false;
      }).catch(function (e) {
        _internalMutation = false;
        console.warn('[pf-cart-rules] rush attribute update failed', e);
        checkbox.disabled = false;
        // Revert visual state on failure.
        checkbox.checked = !on;
        paintToggle(!on);
      });
    });

    refreshRushToggle();
  }

  function paintToggle(on) {
    var slider = document.getElementById('pf-rush-slider');
    var knob   = document.getElementById('pf-rush-knob');
    if (!slider || !knob) return;
    if (on) {
      slider.style.background = '#FF5500';
      knob.style.left = '25px';
    } else {
      slider.style.background = '#ccc';
      knob.style.left = '3px';
    }
  }

  function refreshRushToggle() {
    var card = document.getElementById('pf-rush-card');
    if (!card) return;
    getCart().then(function (cart) {
      // Compute total stickers from print line items (rush is cart-wide).
      var totalStickers = 0;
      (cart.items || []).forEach(function (it) {
        var iid = itemIdOf(it);
        var per = STICKERS_PER_SKU[iid];
        if (per) totalStickers += per * (it.quantity || 0);
      });
      var attrs = (cart && cart.attributes) || {};
      var on = (attrs.rush_production === 'on' || attrs.rush_production === true);
      // Cache for next mount (covers cart-page section swaps where the DOM
      // gets recreated from scratch). See `_lastRushOn` declaration above.
      _lastRushOn = on;
      // Hide entirely if there are no print items — rush only applies to prints.
      if (totalStickers === 0) {
        card.style.display = 'none';
        return;
      }
      card.style.display = 'flex';
      var checkbox = document.getElementById('pf-rush-switch');
      if (checkbox) checkbox.checked = on;
      paintToggle(on);
      // Live price preview.
      var perStickerCount = Math.max(0, totalStickers - 32);
      var perDollars = (perStickerCount * 0.25);
      var total = 50 + perDollars;
      var preview = document.getElementById('pf-rush-price');
      if (preview) {
        preview.innerHTML =
          '<strong style="color:#FF5500;">+$' + total.toFixed(2) + '</strong>' +
          ' <span style="color:#888;">' +
            '($50 base' +
            (perStickerCount > 0 ? ' + $' + perDollars.toFixed(2) + ' for ' + perStickerCount + ' extra stickers' : '') +
            ', ' + totalStickers + ' stickers in cart)' +
          '</span>';
      }
    }).catch(function () {});
  }

  // -------------------------------------------------------------------------
  // Split-delivery banner — cart page + cart drawer.
  //
  // Tells the customer how their order will be delivered when the cart
  // contains a mix of digital downloads (instant email) and physical
  // printed bags (production + shipping). Five cases:
  //   A — digital only          → email within 15 min
  //   B — physical only, no rush → ship after 7–10 business day production
  //   C — physical only + rush   → ship after 3–5 business day production
  //   D — mixed, no rush         → both messages
  //   E — mixed + rush           → both messages (rush variant)
  //
  // Detection: digital = product handle matches DIGITAL_HANDLES / patterns,
  //   OR requires_shipping === false (excluding rush variants).
  // Physical = _item_id matches print_* OR requires_shipping === true.
  // Rush = the rush base-fee variant is present in the cart.
  // -------------------------------------------------------------------------
  function isDigitalLine(line) {
    if (!line) return false;
    var vid = line.variant_id || line.id;
    if (RUSH_VARIANT_IDS[vid] || LEGACY_RUSH_VARIANTS[vid]) return false;
    var handle = (line.handle || '').toLowerCase();
    if (DIGITAL_HANDLES[handle]) return true;
    for (var i = 0; i < DIGITAL_HANDLE_PATTERNS.length; i++) {
      if (DIGITAL_HANDLE_PATTERNS[i].test(handle)) return true;
    }
    // Fallback: anything that doesn't require shipping (and isn't rush) is
    // treated as digital. Catches new download SKUs we forget to list above.
    if (line.requires_shipping === false) return true;
    return false;
  }
  function isPhysicalLine(line) {
    if (!line) return false;
    var vid = line.variant_id || line.id;
    if (RUSH_VARIANT_IDS[vid] || LEGACY_RUSH_VARIANTS[vid]) return false;
    var iid = itemIdOf(line);
    if (iid && PRINT_QTY[iid]) return true;
    if (line.requires_shipping === true) return true;
    return false;
  }
  function classifyCart(cart) {
    var items = (cart && cart.items) || [];
    var hasDigital = false, hasPhysical = false, hasRush = false;
    for (var i = 0; i < items.length; i++) {
      var line = items[i];
      var vid = line.variant_id || line.id;
      if (RUSH_VARIANT_IDS[vid]) { hasRush = true; continue; }
      if (isDigitalLine(line)) { hasDigital = true; continue; }
      if (isPhysicalLine(line)) { hasPhysical = true; continue; }
    }
    // Also check the cart attribute toggle (set BEFORE planRushSync adds
    // the line items, so the banner can update in the same tick).
    var attrs = (cart && cart.attributes) || {};
    if (attrs.rush_production === 'on' || attrs.rush_production === true) hasRush = true;
    var which = null;
    if (hasDigital && !hasPhysical) which = 'A';
    else if (!hasDigital && hasPhysical && !hasRush) which = 'B';
    else if (!hasDigital && hasPhysical && hasRush) which = 'C';
    else if (hasDigital && hasPhysical && !hasRush) which = 'D';
    else if (hasDigital && hasPhysical && hasRush) which = 'E';
    return { hasDigital: hasDigital, hasPhysical: hasPhysical, hasRush: hasRush, which: which };
  }
  function bannerCopyFor(which) {
    switch (which) {
      case 'A':
        return '🎉 Your order is digital — delivered to your email within 15 minutes of checkout.';
      case 'B':
        return '📦 Your printed bags ship after 7–10 business day production. Add Rush Production to cut that to 3–5 business days.';
      case 'C':
        return '⚡ Rush Production active — your bags ship after 3–5 business day production.';
      case 'D':
        return '📨 Digital items: delivered to your email within 15 minutes. 📦 Printed bags: ship after 7–10 business day production. Add Rush to cut to 3–5 days.';
      case 'E':
        return '📨 Digital items: delivered to your email within 15 minutes. ⚡ Rush Production active — printed bags ship after 3–5 business day production.';
      default:
        return '';
    }
  }

  function buildSplitBanner(id) {
    var el = document.createElement('div');
    el.id = id;
    el.className = 'pf-split-delivery-banner';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.style.cssText = [
      'margin:14px auto',
      'max-width:720px',
      'background:#fff4e6',
      'border-left:4px solid #FF5500',
      'border-radius:8px',
      'padding:12px 16px',
      'font-family:"Barlow Condensed",Arial,sans-serif',
      'font-size:14px',
      'line-height:1.5',
      'color:#1a1a1a',
      'box-shadow:0 2px 8px rgba(255,85,0,0.10)'
    ].join(';');
    el.innerHTML = '<span class="pf-split-delivery-msg"></span>';
    return el;
  }

  function placeOnCartPage(banner) {
    // Insert at the TOP of the cart-items region. Try Horizon, Dawn, then
    // generic selectors. If nothing matches, fall back to top of <main>.
    var anchor =
      document.querySelector('cart-items-component') ||
      document.querySelector('cart-items') ||
      document.querySelector('#main-cart-items') ||
      document.querySelector('.cart__items') ||
      document.querySelector('section[id^="shopify-section"] form[action*="/cart"]') ||
      document.querySelector('main') ||
      document.body;
    if (!anchor) return false;
    if (anchor.parentNode && anchor !== document.body) {
      anchor.parentNode.insertBefore(banner, anchor);
    } else {
      anchor.insertBefore(banner, anchor.firstChild);
    }
    return true;
  }

  function placeInCartDrawer(banner) {
    // Horizon cart drawer uses <cart-drawer-component> + <dialog ref="dialog">.
    // Other themes use <cart-drawer>. Insert at the top of the drawer body.
    var dialog =
      document.querySelector('cart-drawer-component dialog') ||
      document.querySelector('cart-drawer dialog') ||
      document.querySelector('cart-drawer-component') ||
      document.querySelector('cart-drawer') ||
      document.querySelector('.cart-drawer') ||
      document.querySelector('.drawer--cart');
    if (!dialog) return false;
    // Avoid inserting OUTSIDE the dialog's visible area — find a sensible
    // inner container. Most themes have an inner wrapper we can prepend to.
    var inner =
      dialog.querySelector('.cart-drawer__inner') ||
      dialog.querySelector('.drawer__inner') ||
      dialog.querySelector('[part="content"]') ||
      dialog.querySelector('form[action*="/cart"]') ||
      dialog;
    inner.insertBefore(banner, inner.firstChild);
    return true;
  }

  function mountSplitDeliveryBanner() {
    // Inject CSS once (keeps inline styles short and lets us hide cleanly).
    if (!document.getElementById('pf-split-delivery-style')) {
      var style = document.createElement('style');
      style.id = 'pf-split-delivery-style';
      style.textContent =
        '.pf-split-delivery-banner[hidden]{display:none !important;}' +
        '@media (prefers-color-scheme: dark){' +
          '.pf-split-delivery-banner{background:#2a1f10 !important;color:#fff4e6 !important;}' +
        '}';
      document.head.appendChild(style);
    }

    var onCartPage = location.pathname === '/cart';
    if (onCartPage && !document.getElementById('pf-split-delivery-banner-cart')) {
      placeOnCartPage(buildSplitBanner('pf-split-delivery-banner-cart'));
    }
    if (!document.getElementById('pf-split-delivery-banner-drawer')) {
      placeInCartDrawer(buildSplitBanner('pf-split-delivery-banner-drawer'));
    }
    refreshSplitDeliveryBanner();
  }

  function refreshSplitDeliveryBanner() {
    var cartEl = document.getElementById('pf-split-delivery-banner-cart');
    var drawerEl = document.getElementById('pf-split-delivery-banner-drawer');
    if (!cartEl && !drawerEl) return;
    getCart().then(function (cart) {
      var c = classifyCart(cart);
      var copy = bannerCopyFor(c.which);
      [cartEl, drawerEl].forEach(function (el) {
        if (!el) return;
        if (!copy) { el.hidden = true; return; }
        el.hidden = false;
        var msg = el.querySelector('.pf-split-delivery-msg');
        if (msg) msg.textContent = copy;
      });
    }).catch(function () {});
  }
  // Expose for debugging / external callers.
  window.pfClassifyCart = classifyCart;
  window.pfMountSplitDeliveryBanner = mountSplitDeliveryBanner;

  // Re-attempt mounting if the drawer/cart DOM is added asynchronously
  // (Horizon hydrates after first paint), AND self-heal if the banner gets
  // wiped later by cart section re-renders (e.g. softRefreshCartPage's async
  // section-innerHTML swap after toggling rush production — that swap can
  // land AFTER pf:cart:normalized has already fired, removing the banner
  // node we just mounted). Keep the observer alive permanently — the
  // callback is cheap (idempotent getElementById guards) and runs at most
  // a few times per cart mutation.
  if (typeof MutationObserver !== 'undefined') {
    try {
      var splitObs = new MutationObserver(function () {
        var needCart = location.pathname === '/cart' && !document.getElementById('pf-split-delivery-banner-cart');
        var needDrawer = !document.getElementById('pf-split-delivery-banner-drawer') &&
          (document.querySelector('cart-drawer-component, cart-drawer, .cart-drawer, .drawer--cart'));
        if (needCart || needDrawer) mountSplitDeliveryBanner();
      });
      splitObs.observe(document.documentElement, { childList: true, subtree: true });
    } catch (_) {}
  }

  // Also normalize when the slide-cart opens (last chance before checkout).
  // Different Shopify themes use different cart-drawer mechanisms; cover the
  // common ones so the mixed-rush banner reliably fires.
  var _origOpened = window.SLIDECART_OPENED;
  window.SLIDECART_OPENED = function () {
    if (_origOpened) try { _origOpened.apply(this, arguments); } catch (_) {}
    normalize();
  };
  // Dawn-style theme drawer (cart-drawer custom element).
  document.addEventListener('cart-drawer:open', function () { normalize(); mountSplitDeliveryBanner(); });
  document.addEventListener('cart:open', function () { normalize(); mountSplitDeliveryBanner(); });
  document.addEventListener('drawer:open', function () { normalize(); mountSplitDeliveryBanner(); });
  // Watch for ANY <cart-drawer> element being shown (Dawn 2.0+).
  if (typeof MutationObserver !== 'undefined') {
    try {
      var obs = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var m = mutations[i];
          if (m.target && m.target.tagName && /CART-DRAWER|CART-NOTIFICATION/.test(m.target.tagName) &&
              m.attributeName === 'open') {
            normalize();
            break;
          }
        }
      });
      obs.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open', 'class'] });
    } catch (_) {}
  }
  // Final safety: re-run normalize whenever the page becomes visible again
  // (handles back/forward, browser tab restore, etc.).
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') normalize();
  });

  // -------------------------------------------------------------------------
  // Apple Pay digital-only hide (P1 launch-blocker fix — 2026-05-21)
  // Shopify's <shopify-accelerated-checkout-cart> renders the cart-page Apple
  // Pay button with hardcoded shippingType:"shipping" + required postalAddress,
  // regardless of cart.requires_shipping. That makes Apple Pay demand a shipping
  // address on digital-only carts and surface "Update Shipping Address" errors.
  // Workaround: toggle a class on <html> when cart is digital-only so CSS can
  // hide the accelerated buttons and reveal a callout pointing to the regular
  // "Check Out" button (the /checkout path correctly handles digital carts).
  // -------------------------------------------------------------------------
  function applyDigitalOnlyCartFlag(cart) {
    try {
      var html = document.documentElement;
      if (!html) return;
      var isDigitalOnly = !!(cart && cart.items && cart.items.length > 0 && cart.requires_shipping === false);
      if (isDigitalOnly) html.classList.add('pf-digital-only-cart');
      else html.classList.remove('pf-digital-only-cart');
    } catch (_) {}
  }
  function refreshDigitalOnlyCartFlag() {
    getCart().then(applyDigitalOnlyCartFlag).catch(function () {});
  }
  // Initial pass on load.
  onReady(refreshDigitalOnlyCartFlag);
  // Re-evaluate after every cart mutation we know about.
  document.addEventListener('pf:cart:normalized', refreshDigitalOnlyCartFlag);
  // Cart drawer / page visibility events — re-check just in case.
  document.addEventListener('cart-drawer:open', refreshDigitalOnlyCartFlag);
  document.addEventListener('cart:open', refreshDigitalOnlyCartFlag);
  document.addEventListener('drawer:open', refreshDigitalOnlyCartFlag);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') refreshDigitalOnlyCartFlag();
  });
})();


// --- _pfCartIconHardNav (REDO3 2026-05-23) ---------------------------------
// Unconditional /cart navigation for the cart icon. Mirrors the same shim in
// layout/theme.liquid but ships via assets/packflow-cart-rules.js so it
// propagates to the CDN within ~1min (vs theme.liquid's 15-60min HTML edge
// cache). Asset cache propagation is fast → Jacques sees the fix nearly
// immediately. Idempotent: if theme.liquid has already installed the gate,
// this just no-ops via the shared window._pfCartIconHardNav flag.
//
// HARD RULE (Jacques 2026-05-23): cart icon ALWAYS goes to /cart. The /cart
// page handles its own auth/UX. No client-side detection of customer state.
(function installPfCartIconHardNavFromAsset(){
  try {
    if (typeof window === 'undefined' || !window || !window.document) return;
    if (window._pfCartIconHardNav) return; // already installed by theme.liquid
    window._pfCartIconHardNav = true;
    window._pfCartIconAuthGate = true; // compat: prevent old gate re-install
    var CART_SELECTOR = [
      '[data-testid="cart-drawer-trigger"]',
      '[data-testid="cart-icon"]',
      'cart-icon',
      'cart-icon button',
      'cart-icon a',
      '.header-actions__cart-icon',
      '.header-actions__cart-icon button',
      '.header-actions__cart-icon a',
      'a.action__cart',
      'a[href$="/cart"]',
      'a[href*="/cart?"]'
    ].join(',');
    function hardNav(e){
      var t = e.target && e.target.closest ? e.target.closest(CART_SELECTOR) : null;
      if (!t) return;
      if (t.id === 'go-to-cart-btn' || (t.closest && t.closest('#go-to-cart-btn'))) return;
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      window.location.href = '/cart';
    }
    document.addEventListener('click', hardNav, true);
    document.addEventListener('keydown', function(e){
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var t = e.target && e.target.closest ? e.target.closest(CART_SELECTOR) : null;
      if (!t) return;
      if (t.id === 'go-to-cart-btn' || (t.closest && t.closest('#go-to-cart-btn'))) return;
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      window.location.href = '/cart';
    }, true);
    function rewriteHrefs(){
      try {
        document.querySelectorAll(CART_SELECTOR).forEach(function(el){
          if (el.tagName === 'A' && el.getAttribute('href') !== '/cart') {
            el.setAttribute('href', '/cart');
          }
        });
      } catch(_){}
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', rewriteHrefs);
    } else {
      rewriteHrefs();
    }
    var n = 0; var t = setInterval(function(){ rewriteHrefs(); if (++n > 12) clearInterval(t); }, 500);
  } catch(_){}
})();
// --- end _pfCartIconHardNav -----------------------------------------------
